// How an intake question's choices behave. Shared by the two places that render them: the
// onboarding wizard (pages/Onboarding.tsx) and the intake page (pages/ComplianceQuestions.tsx).
//
// Both used to append a free-text "Other" radio or checkbox to every choice question, whatever
// it asked. On a controlled question that is worse than untidy: the answer is mapped back to an
// enum key server-side (backend/src/lib/intakeAnswerSync.ts), so "Other: 6,000 barrels" cannot
// be read, the profile key stays empty, and the designation threshold it feeds stays unanswered
// with nothing on screen to say so. It also produced visible nonsense — an "Other" beside Yes and
// No, and a second "Other" under a list whose last option was already "Other unit".

/** A question, reduced to the fields these rules read. */
export interface ChoiceQuestion {
  questionType: "TEXT" | "SINGLE_CHOICE" | "MULTI_CHOICE";
  orgProfileField: string | null;
}

/** Profile destinations that hold a controlled value rather than free text. `size` is the headcount
 *  band that billing headcount and the Jersey DP tier read, so it is one of the bands or nothing. */
const CONTROLLED_FIELDS = ["sectorCode", "geography", "regulations", "size"];

/** "Other", "Other unit", "None of these", "No, none of them" — an option that already catches the rest. */
const CATCH_ALL = /^(other|none|no,? none)\b/i;

/** An option that rules the others out: ticking "None of these" and a licence contradicts itself. */
export function isExclusiveOption(option: string): boolean {
  return /^(none|no,? none)\b/i.test(option.trim());
}

export function isYesNoOptions(options: string[]): boolean {
  return options.length === 2 && options[0] === "Yes" && options[1] === "No";
}

/**
 * Whether to offer a free-text "Other" alongside a question's own options.
 *
 * No for a controlled question, for Yes/No, and for a list that already ends in its own catch-all.
 * Yes for the rest — an open-ended list ("which cloud providers do you use?") is exactly where a
 * free-text answer is worth having, and those answers are stored verbatim.
 */
export function allowsFreeText(question: ChoiceQuestion, options: string[]): boolean {
  const field = question.orgProfileField ?? "";
  if (field.startsWith("dpProfile.")) return false;
  if (CONTROLLED_FIELDS.includes(field)) return false;
  if (isYesNoOptions(options)) return false;
  return !options.some((o) => CATCH_ALL.test(o.trim()));
}

/**
 * The selection after ticking `option` in a multi-choice list, honouring exclusivity both ways:
 * an exclusive option clears everything else, and any other option clears the exclusive ones.
 */
export function toggleWithExclusivity(selected: Set<string>, option: string): Set<string> {
  if (selected.has(option)) {
    const next = new Set(selected);
    next.delete(option);
    return next;
  }
  if (isExclusiveOption(option)) return new Set([option]);
  return new Set([...[...selected].filter((o) => !isExclusiveOption(o)), option]);
}
